import { Enum_LiveStream_Status } from '@core/api'
import merge from '@core/lodash/merge'
import { logInfoCollapsed } from '@core/logging/helpers'
import { pruneNilValues } from '@core/object'
import { shouldBlockEndpointWithConsent } from '@core/tracking/consent'
import {
  persistLastEngagedActiveLiveStreamId,
  persistLastEngagedLivestreamId,
  persistLastEngagedReplayLiveStreamId,
  persistLastEngagedVideoId,
  persistLastEngagementTimestamp,
} from '@core/tracking/funnelTracking'
import { getGlobalEventProperties } from '@core/tracking/senders/helpers/globalEventProperties'
import type { ISendPixelTracking } from '@core/tracking/types'

import { sendBeaconPixel } from './helpers/beacon'
import { getGlobalTrackingFields } from './helpers/globalTrackingFields'

/**
 * Sends a pixel tracking event to the backend. It will attach the client
 * metadata to the payload.
 */
export async function sendPixelTracking({
  endpoint,
  data = {},
  host,
}: ISendPixelTracking): Promise<Response | boolean | void> {
  /**
   * Handle funnel tracking persistence of last engaged video and livestream ids
   * based on whether the tracking endpoint is considered being based on user
   * engagement.
   */
  if (endpoint.isUserEngagement) {
    persistLastEngagementTimestamp()
    if (data?.event_properties?._video_id) {
      persistLastEngagedVideoId(data?.event_properties?._video_id)

      if (data?.event_properties?._live_stream_id) {
        persistLastEngagedLivestreamId(data?.event_properties?._live_stream_id)
        if (
          data?.event_properties?.live_stream_status ===
          Enum_LiveStream_Status.ACTIVE
        ) {
          persistLastEngagedActiveLiveStreamId(
            data?.event_properties?._live_stream_id,
          )
        } else if (
          data?.event_properties?.live_stream_status ===
          Enum_LiveStream_Status.REPLAY
        ) {
          persistLastEngagedReplayLiveStreamId(
            data?.event_properties?._live_stream_id,
          )
        }
      }
    }
  }

  if (shouldBlockEndpointWithConsent(endpoint.consent)) {
    return
  }

  const pixelTrackingFields = {
    ...getGlobalTrackingFields(),
    event_properties: getGlobalEventProperties(),
  }

  const mergedData = pruneNilValues(merge({}, pixelTrackingFields, data))

  logInfoCollapsed(`Pixel ${endpoint.url}`, mergedData)

  return sendBeaconPixel({ path: endpoint.url, data: mergedData, host })
}
